import { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { storiesPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll(".reveal").forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function Stories() {
  const revealRef = useRevealOnScroll();
  const data = storiesPageData;
  const [activeTag, setActiveTag] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Unique tags
  const tags = useMemo(() => {
    const allTags = data.stories.map((s) => s.tag);
    return ["all", ...Array.from(new Set(allTags))];
  }, [data.stories]);

  const filteredStories = useMemo(() => {
    return data.stories.filter((s) => {
      const tagMatch = activeTag === "all" || s.tag === activeTag;
      const q = searchQuery.trim().toLowerCase();
      const searchMatch =
        !q ||
        s.title.toLowerCase().includes(q) ||
        s.identity.toLowerCase().includes(q) ||
        s.concern.toLowerCase().includes(q) ||
        s.excerpt.toLowerCase().includes(q);
      return tagMatch && searchMatch;
    });
  }, [data.stories, activeTag, searchQuery]);

  return (
    <>
      <SEO
        title="真實故事｜時光逆行駭客｜客戶逆齡旅程全記錄"
        description="深入每一位客戶的逆齡旅程，了解他們從評估到調整的完整過程。每一段故事，都是個人化的老化程式除錯記錄。"
        keywords="真實故事,逆齡旅程,醫美故事,曾驛翔,時光逆行駭客,老化除錯"
        canonicalPath="/stories"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "真實故事｜時光逆行駭客",
          "description": "深入每一位客戶的逆齡旅程，個人化的老化程式除錯記錄。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg" ref={revealRef}>
      {/* ===== HERO ===== */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 circuit-bg opacity-20" />
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center max-w-4xl mx-auto">
            <span className="section-label reveal opacity-0 translate-y-6 transition-all duration-700">
              {data.heroTagline}
            </span>
            <h1 className="mt-5 text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-ghost reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
              {data.heroTitle}
              <span className="text-gradient-gold">{data.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75] reveal opacity-0 translate-y-6 transition-all duration-700 delay-200">
              {data.heroDesc}
            </p>
          </div>
        </div>
      </section>

      {/* ===== LEGAL NOTICE ===== */}
      <section className="py-8 md:py-10 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
            <div className="card-cyber p-5 md:p-6 border-l-4 border-l-neon-cyan/40">
              <div className="flex items-start gap-3">
                <i className="ri-shield-check-line text-neon-cyan text-xl shrink-0 mt-0.5" />
                <p className="text-ghost-dim text-[1rem] leading-[1.7]">{data.legalNote}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FILTER BAR ===== */}
      <section className="pt-10 md:pt-12 pb-4">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
            {/* Tag filter */}
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeTag === tag
                      ? "bg-cyber-gold/15 text-cyber-gold border border-cyber-gold/30"
                      : "bg-abyss-700/50 text-ghost-dim border border-abyss-600 hover:border-ghost-dim/30 hover:text-ghost"
                  }`}
                >
                  {tag === "all" ? "全部" : tag}
                </button>
              ))}
            </div>
            {/* Search */}
            <div className="relative max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-ghost-dim text-base" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜尋故事標題、身份或困擾..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-abyss-700 border border-abyss-600 text-ghost text-base focus:outline-none focus:border-cyber-gold/50 placeholder:text-ghost-dim"
              />
            </div>
            {filteredStories.length === 0 && (
              <p className="mt-4 text-ghost-dim text-base">沒有符合條件的故事。</p>
            )}
          </div>
        </div>
      </section>

      {/* ===== LONG-FORM STORIES ===== */}
      <section className="py-8 md:py-12">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="space-y-16 md:space-y-20 max-w-4xl mx-auto">
            {filteredStories.map((story, idx) => (
              <article
                key={story.id}
                className="reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                {/* Tag + Title */}
                <div className="mb-6">
                  <span
                    className="inline-block px-3 py-1 rounded-lg text-base font-medium mb-4"
                    style={{
                      background: `${story.tagAccent}10`,
                      color: story.tagAccent,
                      border: `1px solid ${story.tagAccent}20`,
                    }}
                  >
                    {story.tag}
                  </span>
                  <h2 className="text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
                    {story.title}
                  </h2>
                  <div className="mt-3 flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{
                        background: `${story.tagAccent}10`,
                        border: `1px solid ${story.tagAccent}20`,
                      }}
                    >
                      <i className="ri-user-line text-base" style={{ color: story.tagAccent }} />
                    </div>
                    <p className="text-ghost-muted text-[1.0625rem]">
                      {story.identity} · {story.concern}
                    </p>
                  </div>
                </div>

                {/* Story body */}
                <div className="card-cyber p-6 md:p-10 transition-all duration-500 hover:-translate-y-1 hover:border-cyber-gold/30">
                  {/* Excerpt */}
                  <div className="mb-8">
                    <p className="text-ghost text-[1.1875rem] leading-[1.75] italic">
                      「{story.excerpt.substring(0, 110)}……」
                    </p>
                  </div>

                  {/* Read more link */}
                  <div className="flex items-center gap-3 mb-6">
                    <Link
                      to={`/stories/${story.id}`}
                      className="inline-flex items-center gap-1.5 text-base font-medium hover:gap-3 transition-all"
                      style={{ color: story.tagAccent }}
                    >
                      閱讀完整故事
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>

                  {/* Compliance */}
                  <div
                    className="px-4 py-3 rounded-xl text-[1rem]"
                    style={{
                      background: `${story.tagAccent}08`,
                      color: `${story.tagAccent}cc`,
                      border: `1px solid ${story.tagAccent}15`,
                    }}
                  >
                    {story.note}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="card-cyber p-10 md:p-16 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.bottomCta.label}</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-[1.0625rem]"
              >
                <i className="ri-line-fill" />
                {data.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold text-[1.0625rem]">
                <i className="ri-calendar-check-line" />
                {data.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}